import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Flag } from "lucide-react";
import { Link } from "react-router-dom";

export const AdminReports = () => {
  const [macroReports, setMacroReports] = useState<any[]>([]);
  const [commentReports, setCommentReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);

    // Fetch macro reports
    const { data: macroData } = await supabase
      .from("reports")
      .select(`
        *,
        macros (
          id,
          name,
          slug
        ),
        profiles:user_id (
          username,
          avatar_url
        )
      `)
      .order("created_at", { ascending: false });

    // Fetch comment reports
    const { data: commentData } = await supabase
      .from("reported_comments")
      .select(`
        *,
        comments (
          id,
          content,
          user_id,
          profiles:user_id (
            username
          )
        ),
        profiles:user_id (
          username,
          avatar_url
        )
      `)
      .order("created_at", { ascending: false });

    setMacroReports(macroData || []);
    setCommentReports(commentData || []);
    setLoading(false);
  };

  const handleResolveMacroReport = async (reportId: string, status: string) => {
    const { error } = await supabase
      .from("reports")
      .update({ status })
      .eq("id", reportId);

    if (error) {
      toast.error("Failed to update report");
    } else {
      toast.success(`Report marked as ${status}`);
      fetchReports();
    }
  };

  const handleResolveCommentReport = async (reportId: string, status: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from("reported_comments")
      .update({ 
        status,
        resolved_at: new Date().toISOString(),
        resolved_by: user?.id
      })
      .eq("id", reportId);

    if (error) {
      toast.error("Failed to update report");
    } else {
      toast.success(`Report marked as ${status}`);
      fetchReports();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading reports...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Flag className="h-6 w-6" />
        <h1 className="text-3xl font-bold">Reports</h1>
      </div>

      <Tabs defaultValue="macros" className="w-full">
        <TabsList>
          <TabsTrigger value="macros">
            Macro Reports ({macroReports.filter(r => r.status === 'open').length})
          </TabsTrigger>
          <TabsTrigger value="comments">
            Comment Reports ({commentReports.filter(r => r.status === 'open').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="macros" className="space-y-4 mt-6">
          {macroReports.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No macro reports</p>
            </Card>
          ) : (
            macroReports.map((report) => (
              <Card key={report.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={report.status === 'open' ? 'destructive' : 'secondary'}>
                        {report.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    
                    <Link 
                      to={`/m/${report.macros?.slug}`}
                      className="text-lg font-semibold hover:text-primary"
                    >
                      {report.macros?.name || 'Deleted Macro'}
                    </Link>
                    
                    <p className="text-sm">
                      <span className="font-medium">Reason:</span> {report.reason}
                    </p>
                    
                    <p className="text-sm text-muted-foreground">
                      Reported by @{report.profiles?.username || 'Anonymous'}
                    </p>
                  </div>

                  {report.status === 'open' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolveMacroReport(report.id, 'resolved')}
                      >
                        Resolve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleResolveMacroReport(report.id, 'actioned')}
                      >
                        Take Action
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="comments" className="space-y-4 mt-6">
          {commentReports.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No comment reports</p>
            </Card>
          ) : (
            commentReports.map((report) => (
              <Card key={report.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={report.status === 'open' ? 'destructive' : 'secondary'}>
                        {report.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <p className="text-sm font-medium mb-1">
                        Comment by @{report.comments?.profiles?.username || 'Unknown'}
                      </p>
                      <p className="text-sm">{report.comments?.content || 'Comment deleted'}</p>
                    </div>
                    
                    <p className="text-sm">
                      <span className="font-medium">Reason:</span> {report.reason}
                    </p>
                    
                    <p className="text-sm text-muted-foreground">
                      Reported by @{report.profiles?.username || 'Anonymous'}
                    </p>
                  </div>

                  {report.status === 'open' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolveCommentReport(report.id, 'resolved')}
                      >
                        Resolve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleResolveCommentReport(report.id, 'actioned')}
                      >
                        Delete Comment
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
